import { useEffect, useState } from "react";
import { API_BASE } from "../../config";
import { fetchWithAuth } from "../../utils/fetchWithAuth";
import { hasRemediationAccess } from "../../config/searchSpaces";

const EMPTY_REMEDIATION_STATS = {
  AINSTEIN_STATUS: "unknown",
  LAST_UPDATE: "",
  CLOSED_CASES: 0,
  TYPES: [],
  LAST_WEEK: null,
  LAST_MONTH: null,
};

const OFFLINE_REMEDIATION_STATS = {
  ...EMPTY_REMEDIATION_STATS,
  AINSTEIN_STATUS: "offline",
};

async function readJsonResponse(response) {
  const text = await response.text();

  if (!text) {
    return null;
  }

  try {
    return JSON.parse(text);
  } catch {
    if (response.ok) {
      throw new Error("Backend did not return valid JSON.");
    }

    return {
      detail: text,
    };
  }
}

function getBackendErrorMessage(data, fallback) {
  return (
    data?.message ||
    data?.detail ||
    data?.error ||
    data?.errors?.[0]?.message ||
    fallback
  );
}

export function useRemediationData(searchSpace) {
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState("");
  const [stats, setStats] = useState(EMPTY_REMEDIATION_STATS);

  useEffect(() => {
    let isCancelled = false;

    if (!hasRemediationAccess(searchSpace)) {
      setLoading(false);
      setStatus("");
      setStats(EMPTY_REMEDIATION_STATS);
      return;
    }

    const fetchData = async () => {
      setLoading(true);
      setStatus("");

      try {
        const res = await fetchWithAuth(
          `${API_BASE}/ainstein/in/remediation-analysis?searchSpace=${encodeURIComponent(
            searchSpace
          )}`,
          {
            method: "GET",
            headers: {
              Accept: "application/json",
            },
          }
        );

        let data = null;

        try {
          data = await readJsonResponse(res);
        } catch (jsonError) {
          if (res.ok) {
            throw jsonError;
          }
        }

        if (!res.ok) {
          throw new Error(
            getBackendErrorMessage(
              data,
              `Failed to load remediation data (status ${res.status}).`
            )
          );
        }

        const remediationData = data?.data || data || {};

        const timeline =
          remediationData.BY_TIMELINE ||
          remediationData.by_timeline ||
          remediationData.byTimeline ||
          [];

        const normalizePeriod = (period) =>
          String(period || "")
            .trim()
            .toLowerCase()
            .replace(/\s+/g, "_");

        const findPeriod = (key) =>
          Array.isArray(timeline)
            ? timeline.find((item) => normalizePeriod(item?.period) === key)
            : null;

        const lastWeekFromTimeline = findPeriod("last_week");
        const lastMonthFromTimeline = findPeriod("last_month");

        const lastWeek =
          remediationData.LAST_WEEK ||
          remediationData.last_week ||
          (lastWeekFromTimeline
            ? {
              NEW_CASES: lastWeekFromTimeline.new_cases ?? null,
              CLOSED_CASES: lastWeekFromTimeline.closed_cases ?? null,
            }
            : null);

        const lastMonth =
          remediationData.LAST_MONTH ||
          remediationData.last_month ||
          (lastMonthFromTimeline
            ? {
              NEW_CASES: lastMonthFromTimeline.new_cases ?? null,
              CLOSED_CASES: lastMonthFromTimeline.closed_cases ?? null,
            }
            : null);

        if (!isCancelled) {
          setStats({
            AINSTEIN_STATUS: remediationData.AINSTEIN_STATUS || "online",
            LAST_UPDATE: remediationData.LAST_UPDATE || "",
            CLOSED_CASES: remediationData.CLOSED_CASES || 0,
            TYPES: Array.isArray(remediationData.TYPES)
              ? remediationData.TYPES
              : [],
            LAST_WEEK: lastWeek,
            LAST_MONTH: lastMonth,
          });

          setStatus("");
        }
      } catch (e) {
        console.error("Cannot fetch remediation data:", e.message);

        if (!isCancelled) {
          setStats(OFFLINE_REMEDIATION_STATS);
          setStatus(e?.message || "Failed to load remediation data.");
        }
      } finally {
        if (!isCancelled) {
          setLoading(false);
        }
      }
    };

    fetchData();

    return () => {
      isCancelled = true;
    };
  }, [searchSpace]);

  return { loading, status, stats };
}